import { Vector2, type PerspectiveCamera, type Scene, type WebGLRenderer } from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js';
import { Pass } from 'three/examples/jsm/postprocessing/Pass.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';

/**
 * Scene pass → bloom → output.
 *
 * Bloom is not decoration here: every emissive element in this world draws with
 * additive blending and no lighting, so bloom is what turns "bright pixels" into
 * "a light source in space". It is the single effect the look depends on.
 *
 * `OutputPass` must terminate the chain. The composer works in linear HDR
 * (half-float targets), and `OutputPass` is what applies tone mapping and the
 * sRGB conversion that `WebGLRenderer` would otherwise have done itself.
 */

/**
 * A pass that renders nothing and swaps nothing; it samples the draw-call counter
 * immediately after the scene pass so scene cost can be reported separately from
 * post-processing cost. Costs one no-op call per frame.
 */
class DrawCallProbe extends Pass {
  calls = 0;

  constructor(private readonly source: WebGLRenderer) {
    super();
    this.needsSwap = false;
    this.enabled = true;
  }

  override render(): void {
    this.calls = this.source.info.render.calls;
  }
}

export interface BloomSettings {
  readonly strength: number;
  readonly radius: number;
  readonly threshold: number;
}

/**
 * Measured, not guessed. The first pass at these values washed the frame out
 * completely — mean luminance 0.62 and empty corners at 0.23 against a
 * clear-colour floor of 0.023 — and isolating the layers showed the scene itself
 * was fine (mean 0.16, corners 0.0227) while bloom alone was responsible.
 *
 * Two of the three numbers were the cause:
 *
 * - `threshold` at 0.12 is below almost everything in a linear HDR buffer, so the
 *   nebula, every star and every ring qualified as a light source. Bloom stopped
 *   being selective and became a global brightener. It has to sit above the
 *   background so that only the mandala and the ripple fronts cross it.
 * - `radius` at 0.62 weights `UnrealBloomPass`'s coarsest mip levels heavily, and
 *   those are frame-wide blurs. That is what lifted the far corners uniformly;
 *   halving it keeps the glow local to what is actually emitting.
 *
 * `strength` was barely at fault and is essentially unchanged.
 */
export const DEFAULT_BLOOM: BloomSettings = Object.freeze({
  strength: 0.9,
  radius: 0.34,
  threshold: 0.55,
});

export class BloomPipeline {
  private readonly composer: EffectComposer;
  private readonly bloomPass: UnrealBloomPass;
  private readonly probe: DrawCallProbe;
  private readonly baseStrength: number;
  private pixelRatio: number;

  constructor(
    renderer: WebGLRenderer,
    scene: Scene,
    camera: PerspectiveCamera,
    settings: BloomSettings = DEFAULT_BLOOM,
  ) {
    this.pixelRatio = renderer.getPixelRatio();
    this.composer = new EffectComposer(renderer);
    this.composer.addPass(new RenderPass(scene, camera));

    this.probe = new DrawCallProbe(renderer);
    this.composer.addPass(this.probe);

    this.bloomPass = new UnrealBloomPass(
      new Vector2(1, 1),
      settings.strength,
      settings.radius,
      settings.threshold,
    );
    this.baseStrength = settings.strength;
    this.composer.addPass(this.bloomPass);

    this.composer.addPass(new OutputPass());
  }

  /** Draw calls issued by the scene pass alone, sampled during the last frame. */
  get sceneCalls(): number {
    return this.probe.calls;
  }

  /** Multiplies the authored bloom strength. Driven by the world's breath. */
  setStrengthScale(scale: number): void {
    this.bloomPass.strength = this.baseStrength * scale;
  }

  resize(width: number, height: number, pixelRatio = this.pixelRatio): void {
    if (pixelRatio !== this.pixelRatio) {
      this.pixelRatio = pixelRatio;
      this.composer.setPixelRatio(pixelRatio);
    }
    this.composer.setSize(width, height);
  }

  render(): void {
    this.composer.render();
  }

  dispose(): void {
    this.composer.dispose();
    this.bloomPass.dispose();
  }
}
